import {
  buildNameRemap,
  remapIds,
  softDeleteLabel,
  softDeleteTopic,
  softDeleteCalendar,
  updateTask,
  updateTopic,
  type Label,
  type Topic,
  type Calendar,
} from "@app/core";
import { getServices } from "../platform.js";

/**
 * Collapse same-name duplicate labels / topics / calendars in local storage,
 * repointing every task reference (and topic parent link) onto the surviving id
 * and soft-deleting the losers. The survivor is chosen deterministically (the
 * smallest id, in `buildNameRemap`) so two synced devices pick the SAME winner
 * and converge instead of tombstoning each other's copy.
 *
 * The reference rewrites + tombstones bump `updated_at`, so they propagate to
 * other devices on the next sync. Returns true when anything changed (the caller
 * then rehydrates the stores). Idempotent: a no-op once everything is unique.
 */
export async function reconcileDuplicateEntities(): Promise<boolean> {
  const { tasks, labels, topics, calendars } = getServices();
  const [labelRows, topicRows, calRows] = await Promise.all([
    labels.listActive(),
    topics.listActive(),
    calendars.listActive(),
  ]);

  const labelRemap = buildNameRemap(labelRows);
  const topicRemap = buildNameRemap(topicRows);
  const calRemap = buildNameRemap(calRows);
  if (!labelRemap.size && !topicRemap.size && !calRemap.size) return false;

  // 1. Repoint every task that references a losing label/topic/calendar.
  for (const t of await tasks.listActive()) {
    const label_ids = remapIds(t.label_ids, labelRemap);
    const topic_ids = remapIds(t.topic_ids, topicRemap);
    const calendar_id = t.calendar_id ? calRemap.get(t.calendar_id) ?? t.calendar_id : t.calendar_id;
    if (label_ids === t.label_ids && topic_ids === t.topic_ids && calendar_id === t.calendar_id) {
      continue;
    }
    await tasks.update(updateTask(t, { label_ids, topic_ids, calendar_id }));
  }

  // 2. Reparent kept topics whose parent was a loser, and let an icon-less
  //    survivor adopt a loser's icon (the survivor is the OLDEST copy, which may
  //    predate per-topic icons — without this, deduping silently drops icons).
  for (const tp of topicRows) {
    if (topicRemap.has(tp.id)) continue; // a loser — tombstoned below
    const fields: { parent_id?: string | null; icon?: string | null } = {};
    if (tp.parent_id && topicRemap.has(tp.parent_id)) {
      fields.parent_id = topicRemap.get(tp.parent_id)!;
    }
    if (!tp.icon) {
      const donor = topicRows.find((l) => topicRemap.get(l.id) === tp.id && l.icon);
      if (donor) fields.icon = donor.icon;
    }
    if (Object.keys(fields).length > 0) {
      await topics.update(updateTopic(tp, fields));
    }
  }

  await tombstone(labelRows, labelRemap, (l: Label) => labels.update(softDeleteLabel(l)));
  await tombstone(topicRows, topicRemap, (t: Topic) => topics.update(softDeleteTopic(t)));
  await tombstone(calRows, calRemap, (c: Calendar) => calendars.update(softDeleteCalendar(c)));

  return true;
}

async function tombstone<T extends { id: string }>(
  rows: T[],
  remap: Map<string, string>,
  del: (row: T) => Promise<void>,
): Promise<void> {
  for (const row of rows) if (remap.has(row.id)) await del(row);
}
